import { useRef, useState } from 'react';
import { Paperclip, X, Loader2, CircleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { imageFilesFrom, type PendingAttachment } from './useAttachmentUploads';

/**
 * Composer attachment area: drag-and-drop, click-to-pick, and paste (images).
 * Stateless about uploading — the parent owns a useAttachmentUploads() instance
 * and passes its items + handlers in.
 */
export function AttachmentDropzone({
  items,
  onAddFiles,
  onRemove,
  disabled
}: {
  items: PendingAttachment[];
  onAddFiles: (files: File[] | FileList, inline?: boolean) => void;
  onRemove: (key: string) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer.files.length) onAddFiles(e.dataTransfer.files);
      }}
      onPaste={(e) => {
        const imgs = imageFilesFrom(e.clipboardData);
        if (imgs.length) { e.preventDefault(); onAddFiles(imgs, true); }
      }}
      className={cn(
        // border-transparent by default → swapping to dashed on drag causes no layout shift
        'mt-1.5 rounded-md border border-transparent px-1 py-1 transition-colors',
        dragOver && 'border-dashed border-primary bg-primary/5'
      )}
    >
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded px-1.5 py-1 text-foreground/80 hover:bg-muted hover:text-foreground disabled:opacity-50"
        >
          <Paperclip className="w-3.5 h-3.5" /> Attach files
        </button>
        <span className="opacity-70">drag &amp; drop or paste images</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files?.length) onAddFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {items.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {items.map((it) => (
            <span
              key={it.key}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
                it.status === 'error' && 'border-destructive/40 text-destructive'
              )}
            >
              {it.status === 'uploading' && <Loader2 className="w-3 h-3 animate-spin" />}
              {it.status === 'error' && <CircleAlert className="w-3 h-3" />}
              <span className="max-w-[12rem] truncate">{it.fileName}</span>
              <button
                type="button"
                onClick={() => onRemove(it.key)}
                aria-label={`Remove ${it.fileName}`}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
